import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AutoComplete, Select } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import {
  getInventaireConfig, getInventaire, getProgression, getConcordance,
  getAffectations, affecterCompteur, retirerCompteur, getUtilisateurs,
} from '../../services/inventaireService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeStatusBadge,
  NdoeStats, NdoeStatCard, NdoeError, NdoeLoading,
} from '../../components/ndoe';
import './inventaires.css';
import './inventaireEquipe.css';

/*
  ÉQUIPES ET PROGRESSION — la vue du pilote pendant un inventaire.

  Deux questions, et deux seulement : QUI compte, et OÙ EN EST-ON. Le détail
  article par article vit dans la session ; on ne le duplique pas ici.

  ⚠ COMPTAGE À L'AVEUGLE : rien sur cet écran ne dit à un compteur ce qu'il
  DEVRAIT trouver. On n'affiche que des totaux de gestes et de paires vues —
  jamais le théorique, jamais l'écart. Sinon le recomptage ne vaut plus rien.

  ⚠ DOUBLE AUTORISATION : un compteur doit avoir un RÔLE autorisé au module
  (paramètre « Qui peut compter ») ET être affecté à CETTE session. Tant que le
  paramètre ne liste que « admin », personne d'autre ne peut être affecté : le
  serveur refuse, et l'écran le dit AVANT qu'on essaie.
*/

const PASSES = [
  {
    value: 1,
    label: '1 · Comptage',
    aide: 'Le premier passage : on compte ce qu’on voit et on met de côté.',
  },
  {
    value: 2,
    label: '2 · Recomptage',
    aide: 'Quelqu’un d’autre repasse derrière, sans voir le premier chiffre.',
  },
  {
    value: 3,
    label: '3 · Arbitrage',
    aide: 'On tranche les articles où les deux passages ne disent pas la même chose.',
  },
];
const nomPasse = (p) => (PASSES.find((x) => x.value === Number(p)) || {}).label || `Passe ${p}`;

const SANS_EQUIPE = '— Sans équipe —';

export default function InventaireEquipe() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [header, setHeader] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [prog, setProg] = useState(null);
  const [affs, setAffs] = useState([]);
  const [desaccords, setDesaccords] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rafraichit, setRafraichit] = useState(false);
  const [error, setError] = useState('');

  // Formulaire d'affectation
  const [choixUser, setChoixUser] = useState(undefined);
  const [choixPasse, setChoixPasse] = useState(1);
  const [choixEquipe, setChoixEquipe] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const estAdmin = !!cfg?.peut_valider;
  const rolesAutorises = useMemo(
    () => (cfg?.roles || []).map((r) => String(r).toLowerCase()),
    [cfg],
  );

  // Le suivi (progression + qui compte) est rechargeable seul : pendant un
  // inventaire, c'est la seule chose qui bouge.
  const chargerSuivi = useCallback(async () => {
    const [{ data: p }, { data: a }, { data: c }] = await Promise.all([
      getProgression(id),
      getAffectations(id),
      getConcordance(id).catch(() => ({ data: [] })),
    ]);
    setProg(p);
    setAffs(Array.isArray(a) ? a : []);
    setDesaccords(Array.isArray(c) ? c : []);
  }, [id]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: h }, { data: c }] = await Promise.all([
        getInventaire(id), getInventaireConfig(),
      ]);
      setHeader(h);
      setCfg(c);
      await chargerSuivi();
      // L'annuaire complet est réservé à l'admin ; un compteur qui consulte
      // l'écran n'en a pas besoin (il ne peut affecter personne).
      if (c?.peut_valider) {
        const { data: u } = await getUtilisateurs().catch(() => ({ data: [] }));
        setUsers(Array.isArray(u) ? u : []);
      }
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || "Impossible de charger l'inventaire.");
    } finally { setLoading(false); }
  }, [id, chargerSuivi]);

  useEffect(() => { charger(); }, [charger]);

  const enCours = header?.statut === 'en_cours';

  // Rafraîchissement automatique pendant la session : le pilote regarde cet
  // écran pour savoir si ça avance, pas pour cliquer sur un bouton.
  useEffect(() => {
    if (!enCours) return undefined;
    const t = setInterval(() => { chargerSuivi().catch(() => {}); }, 30000);
    return () => clearInterval(t);
  }, [enCours, chargerSuivi]);

  const rafraichir = async () => {
    setRafraichit(true);
    try { await chargerSuivi(); } catch (e) { toast.error('Actualisation impossible.'); }
    finally { setRafraichit(false); }
  };

  // --- Équipes ---------------------------------------------------------------
  const equipes = useMemo(() => {
    const map = new Map();
    affs.forEach((a) => {
      const cle = a.equipe || SANS_EQUIPE;
      if (!map.has(cle)) map.set(cle, { nom: cle, membres: [], gestes: 0, paires: 0 });
      const g = map.get(cle);
      g.membres.push(a);
      g.gestes += Number(a.gestes) || 0;
      g.paires += Number(a.paires) || 0;
    });
    const list = [...map.values()];
    list.forEach((g) => g.membres.sort(
      (x, y) => (x.passe - y.passe) || String(x.username || '').localeCompare(String(y.username || '')),
    ));
    // Les équipes nommées d'abord ; « sans équipe » ferme la marche.
    return list.sort((x, y) => {
      if (x.nom === SANS_EQUIPE) return 1;
      if (y.nom === SANS_EQUIPE) return -1;
      return x.nom.localeCompare(y.nom);
    });
  }, [affs]);

  // Quand quelqu'un est affecté à PLUSIEURS passes, le serveur retient la plus
  // haute pour tous ses comptages. Autant le dire ici : sinon l'admin croit que
  // la personne compte encore en passe 1.
  const passeEffective = useMemo(() => {
    const m = new Map();
    affs.forEach((a) => {
      const n = Number(a.passe);
      m.set(a.id_user, Math.max(n, m.get(a.id_user) || 0));
    });
    return m;
  }, [affs]);

  const nomsEquipes = useMemo(
    () => [...new Set(affs.map((a) => a.equipe).filter(Boolean))].map((v) => ({ value: v })),
    [affs],
  );

  // Paires comptées par des personnes qui ne sont PLUS affectées : leurs gestes
  // restent au journal (une affectation retirée n'efface rien). Sans cette
  // note, le total des équipes ne collerait pas au total général.
  const pairesOrphelines = useMemo(() => {
    if (!prog) return 0;
    const affectees = affs.reduce((s, a) => s + (Number(a.paires) || 0), 0);
    return Math.max(0, (Number(prog.paires_comptees) || 0) - affectees);
  }, [prog, affs]);

  // --- Candidats à l'affectation --------------------------------------------
  const options = useMemo(() => {
    const dejaPasse = new Set(affs.map((a) => `${a.id_user}:${a.passe}`));
    return users
      .map((u) => {
        const roleOk = rolesAutorises.includes(String(u.role || '').toLowerCase());
        const deja = dejaPasse.has(`${u.id}:${choixPasse}`);
        return {
          value: u.id,
          disabled: !roleOk || deja,
          label: (
            <span className="ie-opt">
              <span>{u.username}</span>
              <small>
                {u.role}
                {!roleOk && ' · rôle non autorisé à compter'}
                {roleOk && deja && ' · déjà sur cette passe'}
              </small>
            </span>
          ),
          recherche: `${u.username} ${u.role}`,
        };
      })
      .sort((a, b) => (a.disabled === b.disabled ? 0 : (a.disabled ? 1 : -1)));
  }, [users, rolesAutorises, affs, choixPasse]);

  const affecter = async () => {
    if (!choixUser) { toast.warn('Choisissez la personne à affecter.'); return; }
    setEnvoi(true);
    try {
      await affecterCompteur(id, {
        id_user: choixUser, passe: choixPasse, equipe: choixEquipe.trim() || null,
      });
      toast.success('Compteur affecté.');
      setChoixUser(undefined);
      await chargerSuivi();
    } catch (e) {
      // Le serveur nomme le rôle et les rôles autorisés : on le montre tel quel.
      toast.error(e.response?.data?.error || "Affectation impossible.");
    } finally { setEnvoi(false); }
  };

  const retirer = async (a) => {
    try {
      await retirerCompteur(id, a.id_user, a.passe);
      toast.success(`${a.username} retiré de la ${nomPasse(a.passe).toLowerCase()}.`);
      await chargerSuivi();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Retrait impossible.');
    }
  };

  if (error && !loading) return <NdoePage><NdoeError text={error} onRetry={charger} /></NdoePage>;
  if (loading || !header) return <NdoePage><NdoeLoading rows={6} /></NdoePage>;

  const articles = Number(prog?.articles) || 0;
  const vus = Number(prog?.articles_touches) || 0;
  const restants = Math.max(0, articles - vus);
  const pct = articles ? Math.round((vus / articles) * 100) : 0;
  const rolesLimites = rolesAutorises.length <= 1;
  // RETENU ≠ VU : un recomptage remplace le premier passage, donc la somme des
  // gestes dépasse le total réel dès qu'une passe 2 est intervenue.
  const retenues = Number(prog?.paires_retenues) || 0;
  const vues = Number(prog?.paires_comptees) || 0;
  const recomptes = Number(prog?.articles_recomptes) || 0;
  const divergences = Number(prog?.divergences) || 0;

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Stock · Inventaires"
        title={`Équipes et progression — ${header.reference}`}
        subtitle="Qui compte, dans quelle passe, et où en est le comptage."
        actions={(
          <>
            <NdoeActionButton variant="secondary" icon={<ReloadOutlined />}
              loading={rafraichit} onClick={rafraichir}>Actualiser</NdoeActionButton>
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}/direct`)}>
              En direct
            </NdoeActionButton>
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}`)}>
              La session
            </NdoeActionButton>
            {enCours && (
              <NdoeActionButton onClick={() => navigate(`/inventaires/${id}/compter`)}>
                Compter
              </NdoeActionButton>
            )}
          </>
        )}
      />

      {!enCours && (
        <p className="pp-note">
          <NdoeStatusBadge kind="custom" tone={header.statut === 'valide' ? 'ok' : 'neutral'}
            label={header.statut === 'valide' ? 'Validé' : 'Abandonné'} />
          {' '}Session fermée : les affectations ne servent plus qu’à lire qui a compté quoi.
        </p>
      )}

      {/* --- Où en est-on ---------------------------------------------------- */}
      <NdoeStats className="pp-stats">
        <NdoeStatCard label="Articles vus" value={`${vus}/${articles}`} gold />
        <NdoeStatCard label="Paires retenues" value={retenues} gold />
        <NdoeStatCard label="Gestes de comptage" value={Number(prog?.gestes) || 0} />
        <NdoeStatCard label="Compteurs affectés" value={affs.length} />
      </NdoeStats>

      <div className="ndoe-card ie-avancement">
        <div className="inv-barre" aria-hidden="true"><i style={{ width: `${pct}%` }} /></div>
        <p className="ie-avancement__txt">
          <b className="num">{pct} %</b> des articles du périmètre ont été touchés au moins une fois.
          {restants > 0 && (
            <>
              {' '}<b className="num">{restants}</b> n’ont encore reçu aucun comptage —
              {' '}<b>à la clôture, un article jamais compté vaut zéro</b>, pas « pas fait ».
              {header.type === 'total' && ' Sur un inventaire total, c’est normal en début de journée : la plupart des références sont déjà à zéro en stock.'}
            </>
          )}
        </p>
      </div>

      {/* --- Par passe -------------------------------------------------------- */}
      <section className="ndoe-card ie-bloc">
        <h3 className="ie-titre">Par passe</h3>
        {prog?.par_passe?.length ? (
          <div className="ie-passes">
            {prog.par_passe.map((p) => (
              <div className="ie-passe" key={p.passe}>
                <span className="ie-passe__nom">{nomPasse(p.passe)}</span>
                <span className="ie-passe__chiffres num">
                  <b>{p.paires}</b> paires · {p.articles} article(s) · {p.gestes} geste(s)
                </span>
                <small>{(PASSES.find((x) => x.value === Number(p.passe)) || {}).aide}</small>
              </div>
            ))}
          </div>
        ) : (
          <p className="inv-aide">Aucun comptage enregistré pour l’instant.</p>
        )}
        <p className="inv-aide ie-note">
          Les passes ne s’additionnent pas : sur un article donné, c’est le passage le plus
          avancé qui fait foi — un recomptage <b>remplace</b> le comptage, un arbitrage remplace
          le recomptage. À l’intérieur d’une même passe, en revanche, tout s’ajoute (20 puis 30
          font 50).
          {recomptes > 0 && vues !== retenues && (
            <> Les compteurs ont vu <b className="num">{vues}</b> paires en tout ;
              l’inventaire en retient <b className="num">{retenues}</b>, la différence étant
              le travail refait.
            </>
          )}
        </p>
      </section>

      {/* --- Concordance : ce que sert un recomptage --------------------------- */}
      {recomptes > 0 && (
        <section className="ndoe-card ie-bloc">
          <h3 className="ie-titre">Concordance des passages</h3>
          <p className="inv-aide">
            <b className="num">{recomptes}</b> article(s) ont été vus par plus d’un passage.
            {divergences > 0
              ? <> Sur <b className="num">{divergences}</b> d’entre eux, les passages ne disent
                pas la même chose — c’est exactement ce qu’un recomptage sert à trouver.</>
              : ' Tous concordent.'}
          </p>
          {desaccords.length > 0 && (
            <div className="ie-desaccords">
              <div className="ie-desaccord ie-desaccord--head">
                <span>Article</span><span>Passages</span><span>Retenu</span><span>Écart</span>
              </div>
              {desaccords.slice(0, 50).map((d) => (
                <div className="ie-desaccord" key={d.id_varianteProduit}>
                  <span className="ie-desaccord__art">
                    <b>{d.nom_produit}</b>
                    <small>
                      {d.taille ? `P${d.taille}` : ''}
                      {d.couleur ? ` · ${d.couleur}` : ''}
                      {d.code_article ? ` · ${d.code_article}` : ''}
                    </small>
                  </span>
                  <span className="num">
                    {d.passes.map((p) => `p${p.passe} : ${p.total}`).join('  →  ')}
                  </span>
                  <span className="num"><b>{d.retenu}</b> <small>(passe {d.retenu_passe})</small></span>
                  <span className={`num ${d.ecart_passes < 0 ? 'is-moins' : 'is-plus'}`}>
                    {d.ecart_passes > 0 ? '+' : ''}{d.ecart_passes}
                  </span>
                </div>
              ))}
              {desaccords.length > 50 && (
                <p className="inv-aide">… et {desaccords.length - 50} autre(s). Les plus gros
                  désaccords sont en tête.</p>
              )}
            </div>
          )}
        </section>
      )}

      {/* --- Qui compte -------------------------------------------------------- */}
      <section className="ndoe-card ie-bloc">
        <h3 className="ie-titre">Qui compte</h3>

        {estAdmin && rolesLimites && (
          <p className="inv-bloquant">
            ⛔ Seul le rôle <b>{rolesAutorises[0] || 'admin'}</b> a accès au module Inventaires :
            vous ne pourrez affecter personne d’autre. Ouvrez
            {' '}<a href="/parametres-boutique">Paramètres → Inventaire → « Qui peut compter »</a>
            {' '}pour y ajouter les rôles qui feront le comptage.
          </p>
        )}

        {!affs.length && <p className="inv-aide">Personne n’est encore affecté à cet inventaire.</p>}

        {equipes.map((g) => (
          <div className="ie-equipe" key={g.nom}>
            <div className="ie-equipe__head">
              <b>{g.nom}</b>
              <span className="num">{g.membres.length} personne(s) · {g.paires} paires · {g.gestes} geste(s)</span>
            </div>
            <div className="ie-membres">
              {g.membres.map((a) => (
                <div className="ie-membre" key={`${a.id_user}-${a.passe}`}>
                  <span className="ie-membre__qui">
                    <b>{a.username || `#${a.id_user}`}</b>
                    <small>{a.role}</small>
                  </span>
                  <span className="ie-membre__passe">
                    {nomPasse(a.passe)}
                    {passeEffective.get(a.id_user) !== Number(a.passe) && (
                      <small className="ie-inactive">
                        inactive — ses comptages vont en {nomPasse(passeEffective.get(a.id_user)).toLowerCase()}
                      </small>
                    )}
                  </span>
                  <span className="ie-membre__chiffres num">
                    {Number(a.paires) || 0} paires · {Number(a.gestes) || 0} geste(s)
                  </span>
                  {estAdmin && enCours && (
                    <NdoeActionButton size="sm" variant="secondary" onClick={() => retirer(a)}>
                      Retirer
                    </NdoeActionButton>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {pairesOrphelines > 0 && (
          <p className="inv-aide ie-note">
            {pairesOrphelines} paire(s) ont été comptées par une personne depuis retirée de
            l’inventaire. Retirer une affectation n’efface aucun geste : ils restent au journal et
            comptent dans le total.
          </p>
        )}

        {estAdmin && enCours && (
          <div className="ie-form">
            <Select
              showSearch
              placeholder="Ajouter une personne…"
              value={choixUser}
              onChange={setChoixUser}
              options={options}
              optionFilterProp="recherche"
              filterOption={(saisie, opt) => String(opt?.recherche || '')
                .toLowerCase().includes(saisie.toLowerCase())}
              style={{ minWidth: 240 }}
              notFoundContent="Aucun utilisateur"
            />
            <Select
              value={choixPasse}
              onChange={setChoixPasse}
              options={PASSES.map((p) => ({ value: p.value, label: p.label }))}
              style={{ minWidth: 170 }}
            />
            <AutoComplete
              value={choixEquipe}
              onChange={setChoixEquipe}
              options={nomsEquipes}
              placeholder="Équipe (optionnel)"
              style={{ minWidth: 190 }}
              maxLength={40}
              allowClear
            />
            <NdoeActionButton loading={envoi} onClick={affecter}>Affecter</NdoeActionButton>
          </div>
        )}
        {estAdmin && enCours && (
          <p className="inv-aide">
            {(PASSES.find((p) => p.value === choixPasse) || {}).aide}
            {' '}L’équipe est un simple nom pour regrouper ceux qui travaillent ensemble — deux
            équipes peuvent compter en même temps, ou l’une après l’autre.
          </p>
        )}
      </section>
    </NdoePage>
  );
}
